import {
  ADDITIONAL_CHARGE_LABELS,
  CONDITIONING_TYPE_LABELS,
  EXTINGUISHING_SYSTEM_LABELS,
  FLOOR_MATERIAL_TYPE_LABELS,
  HEATING_TYPE_LABELS,
  MATERIAL_TYPE_LABELS,
  RELIABILITY_TYPE_LABELS,
  VENTILATION_TYPE_LABELS,
} from 'shared/constants/buildingLabels';
import { NBSP } from 'shared/constants/text';
import type { IBuildingCard, IBuildingElectricity } from 'shared/types/buildingCard';
import { formatDecimal, formatNumber, noBreak } from 'shared/utils/format';

export interface ITechRow {
  name: string;
  value: string;
}

export interface ITechSection {
  /** Арендаторы через «, »; null — блока нет */
  tenants: string | null;
  /** «Технические характеристики»; null — блока нет */
  tech: {
    /** Непустые группы по порядку: доп. оплата / потолки, стены, перекрытия / вентиляция … пожаротушение */
    groups: ITechRow[][];
    /** null — подзаголовка «Электроснабжение» нет */
    electricity: ITechRow[] | null;
  } | null;
}

/** Подписи списка через «, », неизвестные значения пропускаем */
const joinLabels = (labels: Partial<Record<string, string>>, values: Array<string | null>): string =>
  values
    .map(value => (value ? labels[value] : undefined))
    .filter(Boolean)
    .join(', ');

/** Строки группы без пустых значений */
const toRows = (rows: Array<[string, string | undefined]>): ITechRow[] =>
  rows.filter((row): row is [string, string] => Boolean(row[1])).map(([name, value]) => ({ name, value }));

function getGroups(card: IBuildingCard): ITechRow[][] {
  const extinguishing = card.extinguishingSystemTypes.length
    ? card.extinguishingSystemTypes
    : [card.extinguishingSystemType];

  return [
    toRows([['Дополнительно оплачиваются', joinLabels(ADDITIONAL_CHARGE_LABELS, card.additionalCharges)]]),
    toRows([
      ['Потолки', card.ceilingHeight ? `${noBreak(formatDecimal(card.ceilingHeight))}${NBSP}м` : undefined],
      ['Стены и несущие конструкции', joinLabels(MATERIAL_TYPE_LABELS, [card.materialType])],
      ['Материал перекрытий', joinLabels(FLOOR_MATERIAL_TYPE_LABELS, [card.floorMaterialType])],
    ]),
    toRows([
      ['Вентиляция', joinLabels(VENTILATION_TYPE_LABELS, [card.ventilationType])],
      ['Кондиционирование', joinLabels(CONDITIONING_TYPE_LABELS, [card.conditioningType])],
      ['Отопление', joinLabels(HEATING_TYPE_LABELS, [card.heatingType])],
      ['Пожаротушение', joinLabels(EXTINGUISHING_SYSTEM_LABELS, extinguishing)],
    ]),
  ].filter(group => group.length);
}

/**
 * Как `show_electricity` монолита. «Система бесперебойного питания» при видимом блоке выводится всегда:
 * в монолите `null` превращается в «нет» — решение пользователя оставить
 */
function getElectricity({ power, load, hasUpsSystem, reliabilityType }: IBuildingElectricity): ITechRow[] | null {
  if (!power && !load && hasUpsSystem === null && !reliabilityType) {
    return null;
  }

  return toRows([
    ['Мощность', power ? `${noBreak(formatNumber(power))}${NBSP}кВт` : undefined],
    ['Нагрузка', load ? `${noBreak(formatNumber(load))}${NBSP}кВа` : undefined],
    ['Система бесперебойного питания', hasUpsSystem ? 'есть' : 'нет'],
    ['Категория электронадежности', joinLabels(RELIABILITY_TYPE_LABELS, [reliabilityType])],
  ]);
}

/**
 * Нижняя секция: «Арендаторы» и «Технические характеристики». `null` — секцию не выводим.
 * В монолите `show_tech` ссылается на несуществующий `show_ventilation` и прячет секцию целиком — исправлено
 */
export function getTechSection(card: IBuildingCard): ITechSection | null {
  const { anchor, miniAnchor, other } = card.tenants;
  const tenants = [anchor, miniAnchor, other].filter(Boolean).join(', ') || null;
  const groups = getGroups(card);
  const electricity = getElectricity(card.electricity);
  const tech = groups.length || electricity ? { groups, electricity } : null;

  return tenants || tech ? { tenants, tech } : null;
}

/** Блок «Технические характеристики» виден: якорь `#tech` и пункт плавающей шапки (задача 6) */
export const isTechVisible = (card: IBuildingCard): boolean => Boolean(getTechSection(card)?.tech);
